import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin } from "lucide-react";
import { getJson } from "@shared/http";
import { PlacesAutocompleteSchema, PlacesDetailsSchema } from "@shared/api-schemas";

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string, details?: AddressDetails) => void;
  onDetailsChange?: (details: AddressDetails) => void;
  onErrorChange?: (hasError: boolean) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export interface AddressDetails {
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  fullAddress: string;
  lat?: number;
  lng?: number;
}

declare global {
  interface Window {
    google: any;
  }
}

export function AddressAutocomplete({
  value,
  onChange,
  onDetailsChange,
  onErrorChange,
  label = "주소",
  placeholder = "주소를 입력하세요 (예: 160 Pitt St, Sydney)",
  required = false,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string>("");

  // Load Google Maps script with new Places API
  useEffect(() => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      setScriptLoaded(true);
      setIsLoading(false);
      return;
    }

    // Use centralized Google Maps loader
    import("@/lib/googleMapsLoader")
      .then(({ loadGoogleMaps }) => {
        loadGoogleMaps()
          .then(() => {
            setScriptLoaded(true);
            setIsLoading(false);
            if (onErrorChange) onErrorChange(false);
          })
          .catch((error) => {
            console.warn("Google Maps API key not configured - autocomplete disabled", error);
            setIsLoading(false);
            if (onErrorChange) onErrorChange(true);
          });
      })
      .catch((error) => {
        console.error("Failed to import googleMapsLoader:", error);
        setIsLoading(false);
        if (onErrorChange) onErrorChange(true);
      });
  }, [onErrorChange]);

  // Handle input change and fetch predictions using Fetch API
  // LocationSelector와 동일한 패턴 사용: 타임아웃 처리 및 에러 처리 개선
  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (newValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setError(""); // Clear error when input is too short
      return;
    }

    try {
      // 타임아웃 설정 (3초) - LocationSelector와 동일
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), 3000)
      );

      // API 호출 - getJson 사용 (LocationSelector와 동일)
      const apiPromise = getJson(
        `/api/places/autocomplete?input=${encodeURIComponent(newValue)}&components=country:au&types=address`,
        {},
        PlacesAutocompleteSchema
      );

      // 타임아웃과 API 호출 경쟁 (LocationSelector와 동일한 패턴)
      const data = await Promise.race([apiPromise, timeoutPromise]) as any;

      console.log("📍 Autocomplete response:", data);

      // 에러 상태 확인
      if (data.status === "REQUEST_DENIED") {
        console.error("❌ Google API Request Denied:", data.error_message);
        const errorMsg = data.error_message || "Google API 요청이 거부되었습니다";
        setError(
          errorMsg.includes("referer restrictions")
            ? "API 키에 referer 제한이 설정되어 있습니다. 서버 사이드 사용을 위해 Google Cloud Console에서 API 키 제한을 수정해주세요."
            : errorMsg
        );
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      // 성공 응답 처리
      if (data && data.status === "OK" && data.predictions && data.predictions.length > 0) {
        console.log("✅ Found", data.predictions.length, "suggestions");
        setSuggestions(data.predictions);
        setShowSuggestions(true);
        setError(""); // Clear error on success
      } else if (data.status === "ZERO_RESULTS") {
        console.log("ℹ️ No suggestions found");
        setSuggestions([]);
        setShowSuggestions(false);
        setError(""); // Clear error - no results is not an error
      } else {
        console.warn("⚠️ Unexpected response status:", data.status);
        setSuggestions([]);
        setShowSuggestions(false);
        setError(""); // Clear error for unexpected status
      }
    } catch (error: any) {
      if (error.message === "Timeout") {
        console.warn("⏱️ Autocomplete API timeout");
        setSuggestions([]);
        setShowSuggestions(false);
        setError(""); // Timeout is not a critical error
      } else {
        console.error("❌ Error fetching suggestions:", error);
        setSuggestions([]);
        setShowSuggestions(false);
        // Only set error for non-timeout errors
        if (!error.message?.includes("Timeout")) {
          setError("주소 검색 중 오류가 발생했습니다. 다시 시도해주세요.");
        }
      }
    }
  };

  // Handle suggestion click
  // LocationSelector와 동일한 패턴: 타임아웃 처리 및 에러 처리 개선
  const handleSuggestionClick = async (placeId: string) => {
    try {
      // 타임아웃 설정 (5초) - Place Details는 더 오래 걸릴 수 있음
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), 5000)
      );

      const apiPromise = getJson(
        `/api/places/details?placeId=${encodeURIComponent(placeId)}`,
        {},
        PlacesDetailsSchema
      );

      // 타임아웃과 API 호출 경쟁
      const data = await Promise.race([apiPromise, timeoutPromise]) as any;

      if (data && data.result) {
        const details = extractAddressDetails(data.result);
        onChange(details.fullAddress, details);

        if (onDetailsChange) {
          onDetailsChange(details);
        }

        setError(""); // Clear error on success
      } else {
        console.warn("⚠️ Place details response missing result:", data);
        setError("주소 상세 정보를 가져올 수 없습니다.");
      }

      setSuggestions([]);
      setShowSuggestions(false);
    } catch (error: any) {
      if (error.message === "Timeout") {
        console.warn("⏱️ Place Details API timeout");
        setError("주소 정보 로딩 시간이 초과되었습니다. 다시 시도해주세요.");
      } else {
        console.error("❌ Error fetching place details:", error);
        setError("주소 상세 정보를 가져오는 중 오류가 발생했습니다.");
      }
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const extractAddressDetails = (place: any): AddressDetails => {
    const components = place.address_components || [];
    let streetNumber = "";
    let route = "";
    let city = "";
    let state = "";
    let postcode = "";
    let country = "";

    components.forEach((component: any) => {
      const types = component.types;

      if (types.includes("street_number")) {
        streetNumber = component.long_name;
      }
      if (types.includes("route")) {
        route = component.long_name;
      }
      if (types.includes("locality")) {
        city = component.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        state = component.short_name;
      }
      if (types.includes("postal_code")) {
        postcode = component.long_name;
      }
      if (types.includes("country")) {
        country = component.long_name;
      }
    });

    const address = [streetNumber, route].filter(Boolean).join(" ");
    const fullAddress = place.formatted_address || "";

    // Extract coordinates
    const lat = place.geometry?.location?.lat;
    const lng = place.geometry?.location?.lng;

    return {
      address,
      city,
      state,
      postcode,
      country,
      fullAddress,
      lat: lat ? (typeof lat === 'function' ? lat() : lat) : undefined,
      lng: lng ? (typeof lng === 'function' ? lng() : lng) : undefined,
    };
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!scriptLoaded && isLoading) {
    return (
      <div className="space-y-2">
        {label && <Label>{label} {required && <span className="text-destructive">*</span>}</Label>}
        <div className="relative">
          <Input
            placeholder={placeholder}
            disabled
            className="pl-10"
          />
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground">주소 자동완성 로딩 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        {label && <Label>{label} {required && <span className="text-destructive">*</span>}</Label>}
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pl-10"
            data-testid="input-address-autocomplete"
          />
        </div>
        <p className="text-xs text-destructive">⚠️ {error} - 주소를 직접 입력해주세요</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && <Label>{label} {required && <span className="text-destructive">*</span>}</Label>}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="pl-10"
          data-testid="input-address-autocomplete"
          autoComplete="off"
        />

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                className="px-4 py-2 hover-elevate cursor-pointer border-b last:border-b-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  console.log("🔍 Selected place:", suggestion.description, suggestion.place_id);
                  handleSuggestionClick(suggestion.place_id);
                }}
                role="option"
                data-testid={`suggestion-${suggestion.place_id}`}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {suggestion.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        예: "160 Midson Road" - 3글자 이상 입력하면 호주 내 주소 목록이 표시됩니다
      </p>
    </div>
  );
}

